import React, { useEffect, useMemo, useState } from 'react';
import { Copy, ExternalLink, Pencil, Phone, Trash2 } from 'lucide-react';
import type { CarContact, CarContactCategory } from '../types';
import {
  CAR_CONTACT_CATEGORIES,
  createCarContact,
  deleteCarContact,
  fetchCarContacts,
  updateCarContact,
} from '../lib/carContactsApi';

type ContactFormState = {
  name: string;
  category: CarContactCategory;
  phone: string;
  website: string;
  notes: string;
};

const INITIAL_FORM: ContactFormState = {
  name: '',
  category: 'Workshop',
  phone: '',
  website: '',
  notes: '',
};

const CarContactsSection: React.FC = () => {
  const [contacts, setContacts] = useState<CarContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | CarContactCategory>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ContactFormState>(INITIAL_FORM);

  useEffect(() => {
    const loadContacts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchCarContacts();
        setContacts(data);
      } catch (err: any) {
        setError(err?.message || 'Failed to load car contacts');
      } finally {
        setLoading(false);
      }
    };

    loadContacts();
  }, []);

  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return contacts.filter((contact) => {
      if (categoryFilter !== 'all' && contact.category !== categoryFilter) return false;
      if (!term) return true;
      return (
        contact.name.toLowerCase().includes(term) ||
        contact.category.toLowerCase().includes(term) ||
        contact.phone.toLowerCase().includes(term) ||
        (contact.website || '').toLowerCase().includes(term)
      );
    });
  }, [categoryFilter, contacts, search]);

  const resetForm = () => {
    setEditingId(null);
    setShowForm(false);
    setForm(INITIAL_FORM);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload = {
      name: form.name,
      category: form.category,
      phone: form.phone,
      website: form.website,
      notes: form.notes,
    };

    if (!payload.name.trim() || !payload.phone.trim() || saving) return;

    try {
      setSaving(true);
      setError(null);

      if (editingId) {
        const updated = await updateCarContact(editingId, payload);
        setContacts((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const created = await createCarContact(payload);
        setContacts((prev) => [...prev, created]);
      }

      resetForm();
    } catch (err: any) {
      setError(err?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (contact: CarContact) => {
    setEditingId(contact.id);
    setShowForm(true);
    setForm({
      name: contact.name,
      category: contact.category,
      phone: contact.phone,
      website: contact.website || '',
      notes: contact.notes || '',
    });
  };

  const handleDelete = async (id: string) => {
    const shouldDelete = window.confirm('Delete this contact?');
    if (!shouldDelete) return;

    try {
      setError(null);
      await deleteCarContact(id);
      setContacts((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) resetForm();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete contact');
    }
  };

  const buildContactCardText = (contact: CarContact): string => {
    const lines = [
      `Car Contact`,
      `Name: ${contact.name}`,
      `Category: ${contact.category}`,
      `Phone: ${contact.phone}`,
    ];

    if (contact.website) lines.push(`Website: ${contact.website}`);
    if (contact.notes) lines.push(`Notes: ${contact.notes}`);

    return lines.join('\n');
  };

  const handleCopyContactCard = async (contact: CarContact) => {
    try {
      await navigator.clipboard.writeText(buildContactCardText(contact));
      setCopiedId(contact.id);
      window.setTimeout(() => {
        setCopiedId((current) => (current === contact.id ? null : current));
      }, 1500);
    } catch {
      setError('Failed to copy contact card');
    }
  };

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8">
          <h1 className="text-3xl font-bold text-theme-primary mb-2">Car Contacts</h1>
          <p className="text-theme-secondary mb-6">Workshops, dealers, parts stores, and useful car-business contacts.</p>

          <div className="mb-6">
            {!showForm ? (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(INITIAL_FORM);
                  setShowForm(true);
                }}
                className="rounded-md bg-primary-600 px-4 py-2 text-white"
              >
                New Contact
              </button>
            ) : (
              <form onSubmit={handleSubmit} className="rounded-lg border border-theme-primary bg-theme-primary p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="Contact name"
                    className="rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                    placeholder="Phone number"
                    className="rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <select
                    value={form.category}
                    onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value as CarContactCategory }))}
                    className="rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-theme-primary"
                  >
                    {CAR_CONTACT_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={form.website}
                    onChange={(event) => setForm((prev) => ({ ...prev, website: event.target.value }))}
                    placeholder="Website (optional)"
                    className="rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <textarea
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                  placeholder="Notes (optional)"
                  rows={2}
                  className="w-full rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={saving || !form.name.trim() || !form.phone.trim()}
                    className="rounded-md bg-primary-600 px-4 py-2 text-white disabled:opacity-50"
                  >
                    {editingId ? 'Update Contact' : 'Add Contact'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-md border border-theme-primary px-4 py-2 text-theme-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search contacts..."
              className="flex-1 min-w-[220px] rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value as 'all' | CarContactCategory)}
              className="rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-theme-primary"
            >
              <option value="all">All categories</option>
              {CAR_CONTACT_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</p>
          )}

          {loading ? (
            <p className="text-theme-secondary">Loading contacts...</p>
          ) : filteredContacts.length === 0 ? (
            <div className="rounded-lg border border-theme-primary bg-theme-primary p-6 text-theme-secondary">
              No contacts found.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredContacts.map((contact) => (
                <div key={contact.id} className="rounded-lg border border-theme-primary bg-theme-primary p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-theme-primary">{contact.name}</h3>
                      <p className="text-sm text-theme-secondary">{contact.category}</p>
                      {contact.notes && <p className="mt-2 text-sm text-theme-secondary">{contact.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleCopyContactCard(contact)}
                        className="rounded-md p-2 text-theme-secondary hover:bg-theme-secondary/10"
                        title="Copy contact card"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStartEdit(contact)}
                        className="rounded-md p-2 text-theme-secondary hover:bg-theme-secondary/10"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(contact.id)}
                        className="rounded-md p-2 text-theme-secondary hover:bg-theme-secondary/10"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <a
                      href={`tel:${contact.phone}`}
                      className="inline-flex items-center gap-2 rounded-md border border-theme-primary px-3 py-1.5 text-sm text-theme-secondary hover:bg-theme-secondary/10"
                    >
                      <Phone className="h-4 w-4" />
                      {contact.phone}
                    </a>
                    {contact.website && (
                      <a
                        href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-md border border-theme-primary px-3 py-1.5 text-sm text-theme-secondary hover:bg-theme-secondary/10"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Visit website
                      </a>
                    )}
                    {copiedId === contact.id && (
                      <span className="text-xs text-green-500">Copied</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarContactsSection;
